/*
 * WS4 Phase 2 — Mobile sticky add-to-cart.
 *
 * The sticky button PROXIES the section's real Add-to-cart button, so it
 * always adds the currently-selected variant through the tested product-form.js
 * path (which opens the cart drawer with the upsell) — no new add logic.
 *
 * - Hidden until the main Add-to-cart scrolls out of view (IntersectionObserver).
 *   While hidden it is set `inert` so its proxy button leaves the tab order /
 *   a11y tree (no duplicate off-screen "Add to cart" for keyboard/SR users).
 * - Price + sold-out state mirror the main product form on variant change
 *   (MutationObserver, coalesced via requestAnimationFrame).
 * - Label stays "Add to cart"/"Sold out" (this theme's add_to_cart locale is
 *   overridden to "Buy Now", so we never copy the main button's text).
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.mainButton = document.getElementById('ProductSubmitButton-' + this.sectionId);
        this.stickyButton = this.querySelector('[data-kz-sticky-add]');
        this.priceTarget = this.querySelector('[data-kz-sticky-price]');
        this.label = this.querySelector('[data-kz-sticky-label]');

        // Without the real button there is nothing safe to proxy — stay hidden.
        if (!this.mainButton || !this.stickyButton) return;

        // Hidden + non-interactive until the main button scrolls out of view.
        this.inert = true;

        this.stickyButton.addEventListener('click', () => {
          if (!this.mainButton.hasAttribute('disabled')) this.mainButton.click();
        });

        this.setupVisibility();
        this.setupSync();
        this.sync();
      }

      setShown(show) {
        this.classList.toggle('is-visible', show);
        this.inert = !show;
      }

      setupVisibility() {
        const target = this.mainButton.closest('.product-form') || this.mainButton;
        // Progressive enhancement: without IntersectionObserver, leave the bar
        // hidden (don't show it permanently overlapping the in-page button).
        if (!('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => this.setShown(!entry.isIntersecting));
          },
          { threshold: 0 }
        );
        this.observer.observe(target);
      }

      setupSync() {
        const root = document.getElementById('MainProduct-' + this.sectionId);
        if (!root) return;
        // The bar lives OUTSIDE <product-info>, so observing the main section
        // for variant re-renders cannot loop back into our own writes.
        this.mutationObserver = new MutationObserver(() => this.scheduleSync());
        this.mutationObserver.observe(root, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['disabled'],
        });
      }

      // Coalesce bursty variant-change mutations into one sync per frame.
      scheduleSync() {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this.sync();
        });
      }

      sync() {
        // Mirror the main price block. Copy the FULL element (outerHTML) so the
        // .price wrapper + its state classes (price--sold-out / price--on-sale)
        // come along — Dawn keys badge + struck-price visibility off those, so
        // copying only innerHTML would unhide the "Sold out"/"Sale" badge.
        const srcPrice = document.querySelector('#shopify-section-' + this.sectionId + ' .price--large');
        if (srcPrice && this.priceTarget) this.priceTarget.innerHTML = srcPrice.outerHTML;

        // Mirror availability from the real button; keep our own labels.
        const disabled = this.mainButton.hasAttribute('disabled');
        this.stickyButton.toggleAttribute('disabled', disabled);
        if (this.label) this.label.textContent = disabled ? (this.label.dataset.sold || 'Sold out') : (this.label.dataset.add || 'Add to cart');
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.mutationObserver) this.mutationObserver.disconnect();
        if (this._raf) cancelAnimationFrame(this._raf);
      }
    }
  );
}
